import { useEffect, useMemo, useState } from 'react';

import { ApiClient, useAsync, useMutation } from '@clientma/core';
import {
  Alert,
  Badge,
  Button,
  Card,
  Drawer,
  Field,
  Input,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from '@clientma/ui';

import { providerApi, type ProviderCountryInput, type ProviderCountryRow } from '../api';

/**
 * How the provider takes money, per country.
 *
 * Everything a customer needs in order to pay lives here — the bank account they transfer to,
 * the name a cheque is made out to, the gateway that serves their card. Getting one of these
 * wrong does not fail loudly: the money goes somewhere, just not into the account anybody is
 * reconciling, and that takes weeks to unwind.
 *
 * Secret keys are deliberately absent. This edits the publishable key, which is designed to be
 * embedded in a page; the secret it pairs with is held in configuration, where a database read
 * cannot reach it.
 */
export default function Countries({ api }: { api: ApiClient }) {
  const provider = useMemo(() => providerApi(api), [api]);

  const countries = useAsync((signal) => provider.countries(signal), [provider]);
  const [editing, setEditing] = useState<string | null>(null);

  if (countries.isLoading) return <Skeleton height={280} />;

  if (countries.error) {
    return (
      <Alert tone="danger" title="Countries could not be loaded">
        {countries.error.message}
      </Alert>
    );
  }

  const rows = countries.data ?? [];

  return (
    <div className="cma-stack">
      <p className="provider-muted">
        A country is offered to customers only when it is switched on and has a way to be paid.
        Anything missing is listed against it.
      </p>

      {rows.map((row) => (
        <CountryRow key={row.id} row={row} onEdit={() => setEditing(row.countryCode)} />
      ))}

      {editing && (
        <CountryDrawer
          api={api}
          code={editing}
          onClose={() => setEditing(null)}
          onSaved={countries.reload}
        />
      )}
    </div>
  );
}

function CountryRow({ row, onEdit }: { row: ProviderCountryRow; onEdit: () => void }) {
  const methods = [
    row.acceptsCard && 'Card',
    row.acceptsBankTransfer && 'Transfer',
    row.acceptsCheque && 'Cheque',
    row.acceptsUpi && 'UPI',
  ].filter(Boolean) as string[];

  return (
    <Card>
      <div className="provider-actionrow">
        <div>
          <div className="provider-strong">
            {row.countryName} · {row.currency}
          </div>

          <div className="provider-muted">
            {row.tenants} {row.tenants === 1 ? 'customer' : 'customers'}
            {methods.length > 0 ? ` · ${methods.join(', ')}` : ' · no way to pay'}
          </div>
        </div>

        <div className="cma-row" style={{ gap: 8 }}>
          {row.isActive ? (
            <Badge tone="success">Live</Badge>
          ) : (
            <Badge tone="neutral">Off</Badge>
          )}

          <Button size="sm" onClick={onEdit}>
            Edit
          </Button>
        </div>
      </div>

      {/* The API works out what is missing. Repeating that logic here would let the two
          disagree, and the screen would be the one telling the comfortable lie. */}
      {row.gaps.length > 0 && (
        <Alert tone="warning" title="Not ready to bill">
          <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
            {row.gaps.map((gap) => (
              <li key={gap}>{gap}</li>
            ))}
          </ul>
        </Alert>
      )}
    </Card>
  );
}

function CountryDrawer({
  api,
  code,
  onClose,
  onSaved,
}: {
  api: ApiClient;
  code: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const provider = useMemo(() => providerApi(api), [api]);
  const toast = useToast();

  const loaded = useAsync((signal) => provider.country(code, signal), [provider, code]);
  const gateways = useAsync((signal) => provider.paymentProviders(signal), [provider]);

  const [draft, setDraft] = useState<ProviderCountryInput | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (loaded.data) setDraft(loaded.data);
  }, [loaded.data]);

  const set = <K extends keyof ProviderCountryInput>(key: K, value: ProviderCountryInput[K]) =>
    setDraft((current) => (current ? { ...current, [key]: value } : current));

  const save = useMutation((input: ProviderCountryInput) => provider.saveCountry(input), {
    onSuccess: () => {
      toast.success(`${draft?.countryName ?? code} saved`);
      onSaved();
      onClose();
    },

    onError: (failure) => setError(failure.message),
  });

  return (
    <Drawer open title={draft?.countryName ?? code} onClose={onClose}>
      {!draft ? (
        <Skeleton height={320} />
      ) : (
        <div className="cma-stack">
          {error && (
            <Alert tone="danger" title="That could not be saved">
              {error}
            </Alert>
          )}

          <label className="cma-row" style={{ gap: 8, fontSize: 'var(--text-sm)' }}>
            <input
              type="checkbox"
              checked={draft.isActive}
              onChange={(event) => set('isActive', event.target.checked)}
            />
            Offer this country to customers
          </label>

          <Field label="Currency" hint="What customers here are billed in.">
            {(id) => (
              <Input
                id={id}
                value={draft.currency}
                maxLength={3}
                onChange={(event) => set('currency', event.target.value.toUpperCase())}
              />
            )}
          </Field>

          <div className="provider-section">Card</div>

          <label className="cma-row" style={{ gap: 8, fontSize: 'var(--text-sm)' }}>
            <input
              type="checkbox"
              checked={draft.acceptsCard}
              onChange={(event) => set('acceptsCard', event.target.checked)}
            />
            Take card payments
          </label>

          {draft.acceptsCard && (
            <>
              <Field
                label="Gateway"
                required
                hint="Only what this build can talk to. A name it does not know disables card payment for the whole country."
              >
                {(id) => (
                  <Select
                    id={id}
                    value={draft.cardProvider ?? ''}
                    onChange={(event) => set('cardProvider', event.target.value || null)}
                  >
                    <option value="">Choose a gateway</option>

                    {(gateways.data ?? []).map((name) => (
                      <option key={name} value={name}>
                        {name}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>

              <Field
                label={draft.cardProvider === 'razorpay' ? 'Key id' : 'Publishable key'}
                hint={
                  draft.cardProvider === 'razorpay'
                    ? 'The rzp_live_… half of the pair. Public by design. Its secret half and the webhook secret live in configuration, never here.'
                    : 'Safe to publish — it is designed to be embedded in a page. The secret key it pairs with lives in configuration, never here.'
                }
              >
                {(id) => (
                  <Input
                    id={id}
                    value={draft.cardPublishableKey ?? ''}
                    placeholder={draft.cardProvider === 'razorpay' ? 'rzp_live_…' : 'pk_test_…'}
                    onChange={(event) => set('cardPublishableKey', event.target.value || null)}
                  />
                )}
              </Field>

              {/*
                The half of the setup that is not on this screen, said on this screen anyway.
                Two secrets and a webhook have to be put somewhere else for any of this to work,
                and a country switched on without them takes no money and says nothing about why
                until a customer presses pay.
              */}
              {/*
                The two secrets, here rather than in a configuration file, because bringing a
                market live and rotating a key after somebody leaves are both things that happen
                on a Tuesday afternoon and neither should need a redeploy.

                Never shown back. The server returns whether one is on file and nothing more, so
                a key cannot leak through a browser, a proxy log or somebody's network tab.
              */}
              <Field
                label="Secret key"
                hint={
                  draft.hasCardSecret
                    ? 'One is on file. Type a new one only to replace it — leaving this blank keeps what is stored.'
                    : 'The secret half of the pair. Stored encrypted, and never shown again.'
                }
              >
                {(id) => (
                  <Input
                    id={id}
                    type="password"
                    autoComplete="new-password"
                    value={draft.cardSecretKey ?? ''}
                    placeholder={draft.hasCardSecret ? '•••••••• on file' : 'paste it here'}
                    onChange={(event) => set('cardSecretKey', event.target.value)}
                  />
                )}
              </Field>

              <Field
                label="Webhook secret"
                hint={
                  draft.hasCardWebhookSecret
                    ? 'One is on file. Without a matching one here, every callback is refused and nothing settles.'
                    : 'The secret you set on the webhook in the gateway dashboard — not the API key.'
                }
              >
                {(id) => (
                  <Input
                    id={id}
                    type="password"
                    autoComplete="new-password"
                    value={draft.cardWebhookSecret ?? ''}
                    placeholder={draft.hasCardWebhookSecret ? '•••••••• on file' : 'paste it here'}
                    onChange={(event) => set('cardWebhookSecret', event.target.value)}
                  />
                )}
              </Field>

              {draft.cardProvider === 'razorpay' && <RazorpaySetup code={draft.countryCode} />}

              <Field
                label="Account reference"
                hint="The provider-side account money settles into, where there is one per country."
              >
                {(id) => (
                  <Input
                    id={id}
                    value={draft.cardAccountReference ?? ''}
                    onChange={(event) => set('cardAccountReference', event.target.value || null)}
                  />
                )}
              </Field>
            </>
          )}

          <div className="provider-section">Bank transfer</div>

          <label className="cma-row" style={{ gap: 8, fontSize: 'var(--text-sm)' }}>
            <input
              type="checkbox"
              checked={draft.acceptsBankTransfer}
              onChange={(event) => set('acceptsBankTransfer', event.target.checked)}
            />
            Take bank transfers
          </label>

          {draft.acceptsBankTransfer && (
            <>
              <Field label="Bank" required>
                {(id) => (
                  <Input
                    id={id}
                    value={draft.bankName ?? ''}
                    onChange={(event) => set('bankName', event.target.value || null)}
                  />
                )}
              </Field>

              <Field label="Account name">
                {(id) => (
                  <Input
                    id={id}
                    value={draft.bankAccountName ?? ''}
                    onChange={(event) => set('bankAccountName', event.target.value || null)}
                  />
                )}
              </Field>

              <Field label="Account number" required>
                {(id) => (
                  <Input
                    id={id}
                    value={draft.bankAccountNumber ?? ''}
                    onChange={(event) => set('bankAccountNumber', event.target.value || null)}
                  />
                )}
              </Field>

              <Field label="SWIFT / BIC" hint="Needed for a payment crossing a border.">
                {(id) => (
                  <Input
                    id={id}
                    value={draft.bankSwiftCode ?? ''}
                    onChange={(event) => set('bankSwiftCode', event.target.value || null)}
                  />
                )}
              </Field>

              {/* India only. A domestic transfer there is routed by IFSC, so without it the
                  customer's bank cannot send the money at all. */}
              {draft.countryCode === 'IN' && (
                <Field label="IFSC" required hint="A domestic Indian transfer is routed by it.">
                  {(id) => (
                    <Input
                      id={id}
                      value={draft.bankIfscCode ?? ''}
                      onChange={(event) => set('bankIfscCode', event.target.value || null)}
                    />
                  )}
                </Field>
              )}
            </>
          )}

          <div className="provider-section">Other methods</div>

          <label className="cma-row" style={{ gap: 8, fontSize: 'var(--text-sm)' }}>
            <input
              type="checkbox"
              checked={draft.acceptsCheque}
              onChange={(event) => set('acceptsCheque', event.target.checked)}
            />
            Accept cheques
          </label>

          {draft.acceptsCheque && (
            <Field
              label="Payable to"
              required
              hint="The exact legal name. A cheque made out to the wrong one is returned by the bank weeks later."
            >
              {(id) => (
                <Input
                  id={id}
                  value={draft.chequePayableTo ?? ''}
                  onChange={(event) => set('chequePayableTo', event.target.value || null)}
                />
              )}
            </Field>
          )}

          {draft.countryCode === 'IN' && (
            <>
              <label className="cma-row" style={{ gap: 8, fontSize: 'var(--text-sm)' }}>
                <input
                  type="checkbox"
                  checked={draft.acceptsUpi}
                  onChange={(event) => set('acceptsUpi', event.target.checked)}
                />
                Accept UPI
              </label>

              {draft.acceptsUpi && (
                <Field label="Virtual payment address" required>
                  {(id) => (
                    <Input
                      id={id}
                      value={draft.upiVirtualAddress ?? ''}
                      placeholder="name@bank"
                      onChange={(event) => set('upiVirtualAddress', event.target.value || null)}
                    />
                  )}
                </Field>
              )}
            </>
          )}

          <div className="provider-section">Invoicing</div>

          <Field label="Billing entity" hint="The legal entity that invoices customers here.">
            {(id) => (
              <Input
                id={id}
                value={draft.billingEntityName ?? ''}
                onChange={(event) => set('billingEntityName', event.target.value || null)}
              />
            )}
          </Field>

          <Field label="Our tax number">
            {(id) => (
              <Input
                id={id}
                value={draft.supplierTaxNumber ?? ''}
                onChange={(event) => set('supplierTaxNumber', event.target.value || null)}
              />
            )}
          </Field>

          <div className="cma-grid cma-grid--2">
            <Field label="Tax label" hint="GST, SST, VAT — what it is called locally.">
              {(id) => (
                <Input
                  id={id}
                  value={draft.taxLabel ?? ''}
                  onChange={(event) => set('taxLabel', event.target.value || null)}
                />
              )}
            </Field>

            <Field label="Tax rate %">
              {(id) => (
                <Input
                  id={id}
                  inputMode="decimal"
                  value={String(draft.taxRate)}
                  onChange={(event) => set('taxRate', Number(event.target.value) || 0)}
                />
              )}
            </Field>
          </div>

          <Field label="Payment terms (days)">
            {(id) => (
              <Input
                id={id}
                inputMode="numeric"
                value={String(draft.paymentTermDays)}
                onChange={(event) => set('paymentTermDays', Number(event.target.value) || 0)}
              />
            )}
          </Field>

          <Field label="Remittance email" hint="Where customers send proof of payment.">
            {(id) => (
              <Input
                id={id}
                type="email"
                value={draft.remittanceEmail ?? ''}
                onChange={(event) => set('remittanceEmail', event.target.value || null)}
              />
            )}
          </Field>

          <Field label="Payment instructions" hint="Printed on the invoice, under the total.">
            {(id) => (
              <Textarea
                id={id}
                rows={3}
                value={draft.paymentInstructions ?? ''}
                onChange={(event) => set('paymentInstructions', event.target.value || null)}
              />
            )}
          </Field>

          <div className="cma-row" style={{ gap: 8 }}>
            <Button
              variant="primary"
              isLoading={save.isRunning}
              onClick={() => void save.run(draft)}
            >
              Save
            </Button>

            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Drawer>
  );
}


/**
 * What else has to be true before Razorpay takes a rupee.
 *
 * Shown rather than documented elsewhere, because "elsewhere" is where setup instructions go to
 * be lost. Three of the four steps happen outside this console — two secrets in configuration
 * and a webhook in Razorpay's own dashboard — and a country saved without them looks configured
 * and takes no money.
 */
function RazorpaySetup({ code }: { code: string }) {
  const country = (code || 'IN').toUpperCase();

  // Where the callback has to point. Built from the browser's own origin rather than typed,
  // because a webhook URL with a typo in it fails silently for ever.
  const webhook = `${window.location.origin.replace(':3003', ':5200')}/api/v1/webhooks/razorpay/${country}`;

  return (
    <Alert tone="info" title="One more thing, and it is not on this page">
      <p>
        <strong>The webhook.</strong> In the Razorpay dashboard, add this URL and subscribe it to{' '}
        <code>payment_link.paid</code>:
      </p>

      <p>
        <code>{webhook}</code>
      </p>

      <p className="cma-hint">
        <code>payment.captured</code>, <code>payment.failed</code> and{' '}
        <code>payment_link.expired</code> are understood as well; anything else is acknowledged
        and ignored. The secret you set on that webhook is the <code>WebhookSecret</code> above,
        not the API key — confusing the two produces an endpoint that refuses every callback and
        payments that never settle.
      </p>
    </Alert>
  );
}
